import { z } from 'zod';
import { moneySchema, availabilityRuleSchema } from '../../schemas';

const tierSchema = z.object({
  minPurchase: moneySchema,
  discountPercentage: z.number().optional(),
  discountAmount: moneySchema.optional(),
  maxDiscount: moneySchema.optional().nullable(),
});

export const discountSchema = z.object({
  _id: z.string(),
  type: z.string().optional(),
  description: z.string(),
  discountType: z.enum(['fixed', 'percentage']),
  discountAmount: moneySchema.optional(),
  discountPercentage: z.number().optional(),
  maxDiscount: moneySchema.optional(),
  minPurchase: moneySchema.optional(),
  tiers: z.array(tierSchema).optional(),
  applicableOrderType: z.array(z.number()),
  _idCategories: z.array(z.string()).optional(),
  validFrom: z.string(),
  validTo: z.string(),
  availabilityRules: z.array(availabilityRuleSchema).optional(),
  usageLimit: z.number().optional().nullable(),
  userUsageLimit: z.number().optional().nullable(),
  usageCount: z.number().optional(),
  status: z.enum(['active', 'expired', 'disabled']).optional(),
  totalDiscountGiven: z.number().optional(),
});

export const createDiscountSchema = z
  .object({
    type: z.string().optional(),
    description: z.string().min(1, 'Description is required'),
    discountType: z.enum(['fixed', 'percentage']),
    discountAmount: moneySchema.optional().nullable(),
    discountPercentage: z.number().optional().nullable(),
    maxDiscount: moneySchema.optional().nullable(),
    minPurchase: moneySchema.optional().nullable(),
    tiers: z.array(tierSchema).optional(),
    applicableOrderType: z.array(z.number()).min(1, 'At least one order type is required'),
    _idCategories: z.array(z.string()).optional(),
    validFrom: z.string().min(1, 'Start date is required'),
    validTo: z.string().min(1, 'End date is required'),
    availabilityRules: z.array(availabilityRuleSchema).optional(),
    usageLimit: z.number().optional().nullable(),
    userUsageLimit: z.number().optional().nullable(),
    bannerImage: z.string().nullable().optional(),
  })
  .superRefine((data, ctx) => {
    const hasTiers = data.tiers && data.tiers.length > 0;

    if (!hasTiers) {
      if (data.discountType === 'percentage' && !data.discountPercentage) {
        ctx.addIssue({
          code: 'custom',
          path: ['discountPercentage'],
          message: 'Discount % is required',
        });
      }

      if (data.discountType === 'fixed' && (!data.discountAmount || !data.discountAmount.value)) {
        ctx.addIssue({
          code: 'custom',
          path: ['discountAmount'],
          message: 'Discount amount is required',
        });
      }
    } else {
      data.tiers!.forEach((tier, index) => {
        if (data.discountType === 'percentage' && !tier.discountPercentage) {
          ctx.addIssue({
            code: 'custom',
            path: ['tiers', index, 'discountPercentage'],
            message: 'Discount % is required',
          });
        }

        if (data.discountType === 'fixed' && (!tier.discountAmount || !tier.discountAmount.value)) {
          ctx.addIssue({
            code: 'custom',
            path: ['tiers', index, 'discountAmount'],
            message: 'Discount amount is required',
          });
        }

        if (!tier.minPurchase?.value) {
          ctx.addIssue({
            code: 'custom',
            path: ['tiers', index, 'minPurchase'],
            message: 'Min purchase is required',
          });
        }
      });
    }
  });
